/**
 * Shared HTTP fetch helper for the desktop app's REST surface.
 *
 * Wraps every API call with:
 *   • the configured base URL (`API_URL` from `@/lib/config`)
 *   • the WorkOS access token (resolved via `getAccessTokenWithRetry`)
 *   • a uniform JSON content type
 *   • parsed error responses (server's `{ error, details }` shape)
 *
 * Every typed wrapper in `apps/desktop/src/api/` builds on this helper.
 * Consumers should NEVER call `fetch()` directly — see
 * `.claude/rules/data-flow.md`.
 */
import { useAuth } from "@workos-inc/authkit-react";
import { useCallback, useMemo } from "react";
import { API_URL } from "@/lib/config";
import { getAccessTokenWithRetry } from "@/lib/auth-token";
import { notifySessionExpired } from "@/lib/session-expired-bus";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly bodyError?: string,
    public readonly bodyDetails?: string,
  ) {
    super(bodyError ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

/**
 * Thrown when the underlying `fetch()` call fails before reaching the
 * server (offline, DNS failure, TLS handshake, CORS preflight reject).
 * Distinct from `ApiError` (HTTP non-2xx) so callers can choose to
 * silence transient connectivity issues — for instance, a polling
 * loop should not destroy the user's already-rendered list when a
 * single tick can't reach the API.
 *
 * The browser's native message ("Failed to fetch", "NetworkError when
 * attempting to fetch resource", etc.) is preserved as `.message` for
 * debugging; consumers that surface user-facing copy should typically
 * render their own friendlier string ("You're offline", "Reconnecting…").
 */
export class NetworkError extends Error {
  /** Underlying error from `fetch()` (typically TypeError). Stored as
   *  a public field instead of `Error.cause` because the desktop's
   *  TypeScript target predates the ES2022 cause property. */
  public readonly underlying?: Error;
  constructor(
    public readonly url: string,
    cause: unknown,
  ) {
    const causeMessage = cause instanceof Error ? cause.message : String(cause);
    super(`Network request failed: ${causeMessage}`);
    this.name = "NetworkError";
    if (cause instanceof Error) this.underlying = cause;
  }
}

export interface ApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  delete: <T = void>(path: string) => Promise<T>;
}

/**
 * React hook returning a memoized API client. Pulls the access token
 * fresh on every call so re-renders don't matter.
 */
export function useApiClient(): ApiClient {
  const { getAccessToken } = useAuth();

  const request = useCallback(
    async <T>(method: string, path: string, body?: unknown): Promise<T> => {
      const token = await getAccessTokenWithRetry(getAccessToken, {
        maxWaitMs: 3000,
        intervalMs: 200,
      });
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const url = `${API_URL}${path}`;
      let res: Response;
      try {
        res = await fetch(url, {
          method,
          headers,
          body: body == null ? undefined : JSON.stringify(body),
        });
      } catch (err) {
        // `fetch` throws TypeError for connectivity failures (offline,
        // DNS, TLS, CORS preflight reject). It's the only thing it
        // throws on this code path — a 4xx/5xx HTTP error reaches the
        // `if (!res.ok)` branch below as a normal rejection. Re-throw
        // as NetworkError so callers can distinguish transient
        // connectivity from a real API failure.
        throw new NetworkError(url, err);
      }

      if (!res.ok) {
        let bodyError: string | undefined;
        let bodyDetails: string | undefined;
        try {
          const parsed = (await res.json()) as {
            error?: string;
            details?: string;
          };
          bodyError = parsed.error;
          bodyDetails = parsed.details;
        } catch {
          /* non-JSON error body */
        }
        // Surface session expiry to the top-level reauth modal. We
        // notify on 401 only — 403 is "you're authenticated but lack
        // permission" which is a different UI affordance (toast) and
        // shouldn't pop a sign-in modal. The bus is throttled so a
        // burst of parallel requests (e.g. Inbox fan-out) raises one
        // modal, not N.
        if (res.status === 401) {
          notifySessionExpired();
        }
        throw new ApiError(res.status, bodyError, bodyDetails);
      }

      // Empty body (e.g. DELETE returning 204): try-parse, fall back to undefined cast.
      const text = await res.text();
      if (!text) return undefined as T;
      return JSON.parse(text) as T;
    },
    [getAccessToken],
  );

  return useMemo<ApiClient>(
    () => ({
      get: (path) => request("GET", path),
      post: (path, body) => request("POST", path, body),
      put: (path, body) => request("PUT", path, body),
      patch: (path, body) => request("PATCH", path, body),
      delete: (path) => request("DELETE", path),
    }),
    [request],
  );
}
